// GFS price comparison by distribution center.
//
// Every store is served by exactly one GFS distribution center (DC) and never switches, so the
// DC is what moves the price of a given item between locations. This page derives each DC's case
// price from the GFS usage data already in the system (spend / cases across the DC's stores),
// so nobody has to re-key a price list each month.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Collapse,
  DatePicker,
  Empty,
  Input,
  Row,
  Select,
  Space,
  Spin,
  Statistic,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { ApartmentOutlined, DownloadOutlined, WarningOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import { useMobile } from '../../hooks/useMobile';
import { formatCurrency } from '../../utils/formatters';
import { CRITICAL_ITEMS_MAPPING } from '../Expenses/constants';
import DcPriceService, {
  DcPriceCell,
  DcPriceData,
  DcPriceHistory,
  DcPriceItem,
  DcPriceWeek,
  dcPriceItemKey,
} from '../../services/DcPriceService';
import { DateRange } from '../../services/DashboardService';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

type Mode = 'week' | 'last4' | 'last8' | 'custom';

const LOWEST_COLOR = '#237804';
const HIGHEST_COLOR = '#a8071a';
const BRAND = '#7a1f2a';

const rangeKey = (w: DateRange) => `${w.startDate}_${w.endDate}`;

// "Jun 15 – 21", cross-month "May 26 – Jun 1", and the year only when it isn't the current year.
const periodLabel = (startISO: string, endISO: string) => {
  const s = dayjs(startISO);
  const e = dayjs(endISO);
  const left = s.format('MMM D');
  const right = s.month() === e.month() ? e.format('D') : e.format('MMM D');
  const year = e.year() !== dayjs().year() ? `, ${e.format('YYYY')}` : '';
  return `${left} – ${right}${year}`;
};

const cases = (v: number) => (Number.isInteger(v) ? v.toLocaleString() : v.toFixed(1));

const csvEscape = (v: string | number | null | undefined) => {
  const s = v == null ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const DcPrices: React.FC = () => {
  const isMobile = useMobile();

  const [weeks, setWeeks] = useState<DateRange[]>([]);
  // period: 'last4' | 'last8' | 'custom' | 'week:<start>_<end>'
  const [period, setPeriod] = useState<string>('');
  const [customRange, setCustomRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [data, setData] = useState<DcPriceData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [criticalOnly, setCriticalOnly] = useState(false);
  const [diffOnly, setDiffOnly] = useState(false);

  // Per-item week-by-week history, loaded on expand. Keyed by item key.
  const [historyByKey, setHistoryByKey] = useState<Record<string, DcPriceHistory>>({});
  const [historyLoading, setHistoryLoading] = useState<Record<string, boolean>>({});

  useEffect(() => {
    (async () => {
      try {
        const ws = await DcPriceService.getWeeks();
        setWeeks(ws);
        if (ws.length > 0) {
          setPeriod(`week:${rangeKey(ws[0])}`); // default: the most recent week
          setCustomRange([dayjs(ws[0].startDate), dayjs(ws[0].endDate)]);
        }
      } catch {
        setError('Failed to load available weeks. Is preprocessed data uploaded?');
      }
    })();
  }, []);

  const mode: Mode = period === 'last4' ? 'last4' : period === 'last8' ? 'last8' : period === 'custom' ? 'custom' : 'week';

  const latestWeek = weeks[0] ?? null;
  const selectedWeek = useMemo<DateRange | null>(() => {
    if (!period.startsWith('week:')) return latestWeek;
    const k = period.slice(5);
    return weeks.find((w) => rangeKey(w) === k) ?? null;
  }, [period, weeks, latestWeek]);

  const activeRange = useMemo<{ startDate: string; endDate: string } | null>(() => {
    if (mode === 'custom') {
      return customRange
        ? { startDate: customRange[0].format('YYYY-MM-DD'), endDate: customRange[1].format('YYYY-MM-DD') }
        : null;
    }
    if (!latestWeek) return null;
    if (mode === 'last4') {
      return { startDate: dayjs(latestWeek.endDate).subtract(27, 'day').format('YYYY-MM-DD'), endDate: latestWeek.endDate };
    }
    if (mode === 'last8') {
      return { startDate: dayjs(latestWeek.endDate).subtract(55, 'day').format('YYYY-MM-DD'), endDate: latestWeek.endDate };
    }
    return selectedWeek ? { startDate: selectedWeek.startDate, endDate: selectedWeek.endDate } : null;
  }, [mode, customRange, latestWeek, selectedWeek]);

  useEffect(() => {
    if (!activeRange) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const d = await DcPriceService.getDcPrices(activeRange.startDate, activeRange.endDate);
        if (!cancelled) setData(d);
      } catch {
        if (!cancelled) {
          setError('Failed to load DC prices.');
          setData(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeRange]);

  const fetchHistory = useCallback(async (key: string) => {
    setHistoryLoading((m) => ({ ...m, [key]: true }));
    try {
      const h = await DcPriceService.getItemHistory(key);
      setHistoryByKey((m) => ({ ...m, [key]: h }));
    } catch {
      /* expand panel shows an empty state */
    } finally {
      setHistoryLoading((m) => ({ ...m, [key]: false }));
    }
  }, []);

  // ---- Filtering --------------------------------------------------------------
  const criticalName = (item: DcPriceItem): string | undefined => CRITICAL_ITEMS_MAPPING[item.description];

  const filteredItems = useMemo<DcPriceItem[]>(() => {
    const items = data?.items ?? [];
    const q = search.trim().toLowerCase();
    return items.filter((it) => {
      if (criticalOnly && !criticalName(it)) return false;
      if (diffOnly && !(it.spreadAmount != null && it.spreadAmount >= 0.01)) return false;
      if (!q) return true;
      return (
        it.description.toLowerCase().includes(q) ||
        (it.itemNumber ?? '').toLowerCase().includes(q) ||
        (criticalName(it) ?? '').toLowerCase().includes(q) ||
        (it.packSize ?? '').toLowerCase().includes(q)
      );
    });
  }, [data, search, criticalOnly, diffOnly]);

  const summary = useMemo(() => {
    const withDiff = filteredItems.filter((it) => it.spreadAmount != null && it.spreadAmount >= 0.01);
    const savings = filteredItems.reduce((s, it) => s + (it.potentialSavings ?? 0), 0);
    const spend = filteredItems.reduce((s, it) => s + (it.totalSpend ?? 0), 0);
    const biggest = withDiff.reduce<DcPriceItem | null>(
      (best, it) => (best == null || (it.spreadPct ?? 0) > (best.spreadPct ?? 0) ? it : best),
      null
    );
    return { count: filteredItems.length, withDiff: withDiff.length, savings, spend, biggest };
  }, [filteredItems]);

  const headerPeriod = activeRange ? periodLabel(activeRange.startDate, activeRange.endDate) : '';

  // ---- Export -------------------------------------------------------------------
  const exportCsv = () => {
    if (!data || !activeRange) return;
    const dcs = data.dcs;
    const head = ['Item #', 'Description', 'Critical Item', 'Pack Size', ...dcs, 'Lowest DC', 'Highest DC', 'Spread $', 'Spread %', 'Cases', 'Potential Savings'];
    const rows = filteredItems.map((it) => [
      it.itemNumber ?? '',
      it.description,
      criticalName(it) ?? '',
      it.packSize ?? '',
      ...dcs.map((dc) => (it.prices[dc] ? it.prices[dc].price.toFixed(2) : '')),
      it.lowestDc ?? '',
      it.highestDc ?? '',
      it.spreadAmount != null ? it.spreadAmount.toFixed(2) : '',
      it.spreadPct != null ? it.spreadPct.toFixed(1) : '',
      cases(it.totalQuantity),
      it.potentialSavings.toFixed(2),
    ]);
    const csv = [head, ...rows].map((r) => r.map(csvEscape).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `gfs-dc-prices_${activeRange.startDate}_${activeRange.endDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ---- Columns ------------------------------------------------------------------
  const colHead = (main: React.ReactNode, opts?: { sub?: string; tip?: string }) => (
    <div style={{ lineHeight: 1.2 }}>
      {opts?.tip ? (
        <Tooltip title={opts.tip}>
          <span style={{ borderBottom: '1px dotted #bfbfbf', cursor: 'help' }}>{main}</span>
        </Tooltip>
      ) : (
        main
      )}
      {opts?.sub && <div style={{ fontSize: 11, fontWeight: 400, color: '#8c8c8c' }}>{opts.sub}</div>}
    </div>
  );

  const renderPriceCell = (cell: DcPriceCell | undefined, dc: string, lowestDc: string | null, highestDc: string | null, compared: boolean) => {
    if (!cell) return <Text type="secondary">—</Text>;
    const isLow = compared && dc === lowestDc;
    const isHigh = compared && dc === highestDc && highestDc !== lowestDc;
    const varies = cell.maxStorePrice - cell.minStorePrice >= 0.01;
    const tip = (
      <div>
        <div>
          {cases(cell.quantity)} cases · {formatCurrency(cell.spend)} · {cell.storeCount} {cell.storeCount === 1 ? 'store' : 'stores'}
        </div>
        {varies && (
          <div>
            Stores in this DC paid {formatCurrency(cell.minStorePrice)} – {formatCurrency(cell.maxStorePrice)} per case
            (catch-weight item or a mid-week price change).
          </div>
        )}
      </div>
    );
    return (
      <Tooltip title={tip}>
        <span style={{ color: isLow ? LOWEST_COLOR : isHigh ? HIGHEST_COLOR : undefined, fontWeight: isLow || isHigh ? 600 : 400, cursor: 'help' }}>
          {formatCurrency(cell.price)}
          {varies && <WarningOutlined style={{ marginLeft: 4, fontSize: 10, color: '#bfbfbf' }} />}
        </span>
      </Tooltip>
    );
  };

  const columns = useMemo<ColumnsType<DcPriceItem>>(() => {
    const dcs = data?.dcs ?? [];
    const cols: ColumnsType<DcPriceItem> = [
      {
        title: 'Item',
        key: 'item',
        fixed: isMobile ? undefined : 'left',
        width: 280,
        sorter: (a, b) => a.description.localeCompare(b.description),
        render: (_: unknown, it) => {
          const crit = criticalName(it);
          return (
            <div style={{ minWidth: 220 }}>
              <div>
                <Text strong>{crit ?? it.description}</Text>
                {crit && (
                  <Tag color={BRAND} style={{ marginLeft: 8, fontSize: 10, lineHeight: '16px' }}>
                    Critical
                  </Tag>
                )}
              </div>
              <Text type="secondary" style={{ fontSize: 11 }}>
                {crit ? `${it.description} · ` : ''}
                {it.itemNumber ? `#${it.itemNumber}` : 'no item #'}
                {it.packSize ? ` · ${it.packSize}` : ''}
              </Text>
            </div>
          );
        },
      },
    ];
    dcs.forEach((dc) => {
      const storeCount = data?.dcStores[dc]?.length ?? 0;
      cols.push({
        title: colHead(dc, { sub: `${storeCount} ${storeCount === 1 ? 'store' : 'stores'}`, tip: `Case price paid by stores served by the ${dc} DC` }),
        key: `dc:${dc}`,
        align: 'right',
        width: 120,
        sorter: (a, b) => (a.prices[dc]?.price ?? -1) - (b.prices[dc]?.price ?? -1),
        render: (_: unknown, it) => renderPriceCell(it.prices[dc], dc, it.lowestDc, it.highestDc, it.dcCount >= 2),
      });
    });
    cols.push(
      {
        title: colHead('Spread', { tip: 'Highest DC price minus lowest DC price for the same item' }),
        key: 'spread',
        align: 'right',
        width: 130,
        sorter: (a, b) => (a.spreadAmount ?? -1) - (b.spreadAmount ?? -1),
        render: (_: unknown, it) =>
          it.spreadAmount == null ? (
            <Text type="secondary">{it.dcCount < 2 ? 'one DC' : '—'}</Text>
          ) : it.spreadAmount < 0.01 ? (
            <Text type="secondary">same</Text>
          ) : (
            <span>
              {formatCurrency(it.spreadAmount)}{' '}
              <Text type="secondary" style={{ fontSize: 11 }}>
                ({(it.spreadPct ?? 0).toFixed(1)}%)
              </Text>
            </span>
          ),
      },
      {
        title: colHead('Cases', { sub: headerPeriod, tip: 'Cases bought across all DCs in the period' }),
        dataIndex: 'totalQuantity',
        key: 'cases',
        align: 'right',
        width: 100,
        sorter: (a, b) => a.totalQuantity - b.totalQuantity,
        render: (v: number) => cases(v),
      },
      {
        title: colHead('Potential Savings', { sub: headerPeriod, tip: 'What would have been saved if every DC had charged the lowest DC price for the cases it sold' }),
        dataIndex: 'potentialSavings',
        key: 'savings',
        align: 'right',
        width: 140,
        defaultSortOrder: 'descend',
        sorter: (a, b) => a.potentialSavings - b.potentialSavings,
        render: (v: number) => (v >= 0.01 ? <Text strong>{formatCurrency(v)}</Text> : <Text type="secondary">—</Text>),
      }
    );
    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isMobile, headerPeriod]);

  // ---- Expand: week-by-week history --------------------------------------------
  const renderHistory = (it: DcPriceItem) => {
    const key = dcPriceItemKey(it);
    const h = historyByKey[key];
    if (historyLoading[key] || !h) {
      return (
        <div style={{ padding: 24, textAlign: 'center' }}>
          <Spin />
        </div>
      );
    }
    if (h.weeks.length === 0) return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No purchase history" />;
    const hcols: ColumnsType<DcPriceWeek> = [
      {
        title: 'Week',
        key: 'week',
        render: (_: unknown, w, i) => (
          <span>
            {periodLabel(w.weekStart, w.weekEnding)}
            {i === 0 && (
              <Tag color={BRAND} style={{ marginLeft: 8 }}>
                Latest
              </Tag>
            )}
          </span>
        ),
      },
      ...h.dcs.map<ColumnsType<DcPriceWeek>[number]>((dc) => ({
        title: dc,
        key: `h:${dc}`,
        align: 'right',
        render: (_: unknown, w) => {
          const compared = Object.keys(w.prices).length >= 2;
          let highest: string | null = null;
          if (compared) {
            highest = Object.entries(w.prices).reduce<[string, number]>((b, [d, c]) => (c.price > b[1] ? [d, c.price] : b), ['', -1])[0];
          }
          return renderPriceCell(w.prices[dc], dc, w.lowestDc, highest, compared);
        },
      })),
      {
        title: 'Spread',
        key: 'spread',
        align: 'right',
        render: (_: unknown, w) =>
          w.spreadAmount == null ? <Text type="secondary">—</Text> : w.spreadAmount < 0.01 ? <Text type="secondary">same</Text> : formatCurrency(w.spreadAmount),
      },
    ];
    return (
      <div style={{ padding: '4px 0' }}>
        <Text type="secondary" style={{ fontSize: 12, fontWeight: 600 }}>
          PRICE HISTORY BY WEEK{' '}
          <Text type="secondary" style={{ fontWeight: 400 }}>
            · {h.weeks.length} weeks · lowest DC in green
          </Text>
        </Text>
        <Table
          size="small"
          rowKey="weekStart"
          columns={hcols}
          dataSource={h.weeks}
          pagination={h.weeks.length > 12 ? { pageSize: 12, size: 'small' } : false}
          scroll={{ x: 'max-content' }}
          style={{ marginTop: 6 }}
        />
      </div>
    );
  };

  // ---- Render ----------------------------------------------------------------------
  const periodOptions = [
    ...weeks.map((w) => ({ value: `week:${rangeKey(w)}`, label: `Week of ${periodLabel(w.startDate, w.endDate)}` })),
    { value: 'last4', label: 'Last 4 weeks (average price)' },
    { value: 'last8', label: 'Last 8 weeks (average price)' },
    { value: 'custom', label: 'Custom range…' },
  ];

  const dcKey = data ? Object.entries(data.dcStores).filter(([dc]) => dc !== 'Unassigned') : [];
  const unassignedInMapping = data?.dcStores['Unassigned'] ?? [];

  return (
    <div style={{ padding: isMobile ? 12 : 24 }}>
      <div style={{ marginBottom: 16 }}>
        <Title level={isMobile ? 4 : 3} style={{ margin: 0 }}>
          <ApartmentOutlined style={{ marginRight: 8, color: BRAND }} />
          GFS Prices by Distribution Center
        </Title>
        <Text type="secondary">
          Case prices each GFS distribution center charged, worked out from what stores actually paid (spend ÷ cases). A store
          always buys from the same DC.
        </Text>
      </div>

      <Card size="small" style={{ marginBottom: 16 }} data-tour="dc-prices-filters">
        <Space wrap size="middle" style={{ width: '100%' }}>
          <Select
            style={{ minWidth: isMobile ? 220 : 280 }}
            value={period || undefined}
            placeholder="Select period"
            options={periodOptions}
            onChange={(v: string) => {
              setPeriod(v);
              if (v === 'custom' && !customRange && latestWeek) {
                setCustomRange([dayjs(latestWeek.startDate), dayjs(latestWeek.endDate)]);
              }
            }}
            showSearch
            optionFilterProp="label"
          />
          {mode === 'custom' && (
            <RangePicker
              value={customRange}
              onChange={(v) => v && v[0] && v[1] && setCustomRange([v[0], v[1]])}
              allowClear={false}
            />
          )}
          <Input.Search
            allowClear
            placeholder="Search item, #, pack size"
            style={{ width: isMobile ? 220 : 260 }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Space>
            <Switch size="small" checked={criticalOnly} onChange={setCriticalOnly} />
            <Text>Critical items only</Text>
          </Space>
          <Space>
            <Switch size="small" checked={diffOnly} onChange={setDiffOnly} />
            <Text>Only items priced differently</Text>
          </Space>
          <Button icon={<DownloadOutlined />} onClick={exportCsv} disabled={!data || filteredItems.length === 0}>
            Export CSV
          </Button>
        </Space>
      </Card>

      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />}

      {data && data.unassignedStoresWithPurchases.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Some stores are not assigned to a distribution center"
          description={
            <span>
              These stores bought from GFS in this period but have no <code>gfsDc</code> in restaurant-name-mapping.json, so
              their prices are left out: <Text strong>{data.unassignedStoresWithPurchases.join(', ')}</Text>.
            </span>
          }
        />
      )}

      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="Items compared" value={summary.count} suffix={data ? <Text type="secondary" style={{ fontSize: 12 }}>of {data.itemCount}</Text> : undefined} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="Priced differently by DC" value={summary.withDiff} suffix={<Text type="secondary" style={{ fontSize: 12 }}>of {summary.count}</Text>} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic
              title={
                <Tooltip title="If every DC had charged the lowest DC price for the cases it sold in this period">
                  <span style={{ borderBottom: '1px dotted #bfbfbf', cursor: 'help' }}>Potential savings</span>
                </Tooltip>
              }
              value={summary.savings}
              precision={2}
              prefix="$"
              valueStyle={{ color: summary.savings >= 1 ? BRAND : undefined }}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic
              title="Largest spread"
              value={summary.biggest ? `${(summary.biggest.spreadPct ?? 0).toFixed(1)}%` : '—'}
              valueStyle={{ fontSize: 20 }}
            />
            {summary.biggest && (
              <Text type="secondary" style={{ fontSize: 11 }} ellipsis>
                {criticalName(summary.biggest) ?? summary.biggest.description}
              </Text>
            )}
          </Card>
        </Col>
      </Row>

      {data && (
        <Collapse
          size="small"
          style={{ marginBottom: 16 }}
          items={[
            {
              key: 'dcs',
              label: (
                <span>
                  Which stores buy from which distribution center{' '}
                  <Text type="secondary">
                    · {dcKey.length} DCs · {dcKey.reduce((n, [, s]) => n + s.length, 0)} stores
                  </Text>
                </span>
              ),
              children: (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {dcKey.map(([dc, stores]) => (
                    <div key={dc}>
                      <Text strong>{dc}</Text>{' '}
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {stores.length} {stores.length === 1 ? 'store' : 'stores'}
                      </Text>
                      <div style={{ marginTop: 4 }}>
                        {stores.map((s) => (
                          <Tag key={s} style={{ marginBottom: 4 }}>
                            {s}
                          </Tag>
                        ))}
                      </div>
                    </div>
                  ))}
                  {unassignedInMapping.length > 0 && (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      No DC assigned yet: {unassignedInMapping.join(', ')}. Set <code>gfsDc</code> in restaurant-name-mapping.json.
                    </Text>
                  )}
                </div>
              ),
            },
          ]}
        />
      )}

      <Card
        size="small"
        title={
          <span>
            Item prices by DC{' '}
            <Text type="secondary" style={{ fontWeight: 400 }}>
              · {headerPeriod}
              {mode !== 'week' ? ' · average price over the period' : ''}
            </Text>
          </span>
        }
        data-tour="dc-prices-table"
      >
        {loading ? (
          <div style={{ padding: 48, textAlign: 'center' }}>
            <Spin size="large" />
          </div>
        ) : !data || filteredItems.length === 0 ? (
          <Empty description={data ? 'No items match the current filters' : 'No GFS data for this period'} />
        ) : (
          <Table<DcPriceItem>
            size="small"
            rowKey={(it) => dcPriceItemKey(it)}
            columns={columns}
            dataSource={filteredItems}
            pagination={{ pageSize: 50, showSizeChanger: true, pageSizeOptions: [25, 50, 100, 200], size: 'small', showTotal: (t) => `${t} items` }}
            scroll={{ x: 'max-content' }}
            expandable={{
              expandedRowRender: renderHistory,
              onExpand: (expanded, it) => {
                const key = dcPriceItemKey(it);
                if (expanded && !historyByKey[key] && !historyLoading[key]) fetchHistory(key);
              },
            }}
          />
        )}
      </Card>
    </div>
  );
};

export default DcPrices;
